import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import AuditLogs from '../shared/AuditLogs';

// Default system message template content (for placeholder/reference)
const DEFAULT_SYSTEM_MESSAGE_TEMPLATE = `IMPORTANT: You must return your response as a valid JSON object. Use the section names from the list below that are RELEVANT to the visit type. If you are creating a section that's not in this list, use camelCase format for consistency.

AVAILABLE SECTION NAMES (use these exact camelCase keys):
- chiefComplaint: Main complaint or reason for visit
- subjective: Subjective findings (SOAP format)
- historyOfPresentIllness: Detailed narrative of present illness
- reviewOfSystems: Review of systems findings
- pastMedicalHistory: Past medical history
- pastSurgicalHistory: Past surgical history
- socialHistory: Social history (smoking, alcohol, occupation, etc.)
- familyHistory: Family medical history
- allergies: Medication and other allergies
- medications: Current medications list
- objective: Objective findings (SOAP format)
- physicalExamination: Physical exam findings
- vitalSigns: Vital signs measurements
- assessment: Clinical assessment and diagnoses
- impression: Clinical impression (alternative to assessment)
- plan: Treatment plan and follow-up
- recommendations: Clinical recommendations
- disposition: Patient disposition (admit, discharge, etc.)
- followUp: Follow-up instructions
- procedures: Procedures performed during visit
- imaging: Imaging studies ordered or reviewed
- labs: Laboratory tests ordered or reviewed
- education: Patient education provided
- counseling: Counseling provided
- orders: Orders placed

STRUCTURE RULES:
1. ONLY use section names from the list above - use exact camelCase keys for consistency
2. Each section value can be a simple string OR a complex object
3. For structured content: {"sectionName": {"content": "main narrative", "additionalFields": "..."}}
4. Include all sections even if empty
5. Use consistent section names across all visits to enable standardization
6. If the transcript doesn't contain medical conversation, explain in assessment.content why a note cannot be generated

EXAMPLES:

Simple format (preferred for basic notes):
{
    "chiefComplaint": "Patient reports chest pain for 2 days",
    "assessment": "Likely musculoskeletal chest pain",
    "plan": "Prescribed ibuprofen 400mg TID, follow up in 1 week"
}

Complex format with nested data (for detailed clinical documentation):
{
    "chiefComplaint": {
        "content": "Main complaint or reason for visit",
        "symptoms": ["symptom1", "symptom2"],  // optional array of key symptoms
        "duration": "duration if mentioned",  // optional
        "severity": "severity if mentioned"  // optional
    },
    "historyOfPresentIllness": {
        "content": "Detailed narrative of present illness",
        "timeline": [  // optional timeline of events
            {"date": "timeframe", "event": "what happened"}
        ],
        "associatedSymptoms": ["symptom1", "symptom2"],  // optional
        "aggravatingFactors": ["factor1", "factor2"],  // optional
        "relievingFactors": ["factor1", "factor2"]  // optional
    },
    "reviewOfSystems": {
        "content": "General ROS narrative",  // optional
        "positive": ["positive finding 1", "positive finding 2"],  // symptoms present
        "negative": ["negative finding 1", "negative finding 2"],  // symptoms denied
        "notReviewed": ["system1", "system2"]  // optional systems not discussed
    },
    "physicalExamination": {
        "content": "General exam findings",
        "vitals": {  // optional vital signs
            "bloodPressure": "value",
            "heartRate": "value",
            "temperature": "value",
            "respiratoryRate": "value",
            "oxygenSaturation": "value",
            "weight": "value",
            "height": "value"
        },
        "systemFindings": {  // optional organized by system
            "general": "appearance and general findings",
            "heent": "head, eyes, ears, nose, throat findings",
            "cardiac": "cardiac findings",
            "respiratory": "respiratory findings",
            "abdomen": "abdominal findings",
            "extremities": "extremity findings",
            "neurological": "neurological findings",
            "skin": "skin findings"
        }
    },
    "assessment": {
        "content": "Clinical assessment narrative",
        "diagnoses": [  // list of diagnoses
            {
                "diagnosis": "diagnosis name",
                "icdCode": "ICD code if mentioned",  // optional
                "type": "primary|secondary|differential"  // optional
            }
        ],
        "clinicalReasoning": "explanation of diagnostic thinking"  // optional
    },
    "plan": {
        "content": "General plan narrative",  // optional
        "medications": [  // optional medications
            {
                "name": "medication name",
                "dose": "dosage",
                "route": "route of administration",
                "frequency": "frequency",
                "duration": "duration if specified",
                "reason": "indication"
            }
        ],
        "diagnostics": [  // optional tests/studies ordered
            {
                "test": "test name",
                "reason": "indication",
                "urgency": "routine|urgent|stat"  // optional
            }
        ],
        "procedures": ["procedure1", "procedure2"],  // optional
        "referrals": [  // optional referrals
            {
                "specialty": "specialty name",
                "reason": "reason for referral",
                "urgency": "routine|urgent"  // optional
            }
        ],
        "followUp": {  // optional follow-up instructions
            "timeframe": "when to follow up",
            "condition": "under what conditions",
            "provider": "with whom"
        },
        "patientEducation": ["education point 1", "education point 2"],  // optional
        "precautions": ["return if...", "watch for..."]  // optional return precautions
    }
}`;

const SystemLLMPromptManagement = ({ isOpen, onClose }) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  
  // Audit trail state - just track which prompts have audit trails open
  const [openAuditTrails, setOpenAuditTrails] = useState(new Set());
  
  // Dropdown data
  const [healthSystems, setHealthSystems] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  // Form state for creating/editing prompts
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    prompt_type: 'system', // Fixed: Changed from 'system_message' to match dropdown options
    content: '',
    json_schema: {},
    health_system_id: '',
    specialty: '',
    is_active: true,
    version: '1.0'
  });

  // Prompt type is locked to 'system' - all LLM templates are system messages
  // The model supports other types for legacy reasons, but the API only accepts 'system'
  const PROMPT_TYPE = 'system';

  useEffect(() => {
    if (isOpen) {
      fetchPrompts();
      fetchDropdownData();
    }
  }, [isOpen]);

  // Auto-clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Auto-clear error message after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const fetchDropdownData = async () => {
    try {
      setLoadingDropdowns(true);
      const [healthSystemsRes, specialtiesRes] = await Promise.all([
        adminApi.get('/admin/health-systems/'),
        adminApi.get('/admin/specialties/')
      ]);
      
      // Handle the nested response structure
      setHealthSystems(healthSystemsRes.data?.health_systems || []);
      setSpecialties(specialtiesRes.data?.specialties || []);
    } catch (err) {
      setError(`Failed to load dropdown data: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.get('/admin/note/system/prompts/');
      setPrompts(response.data || []);
    } catch (err) {
      setError(`Failed to fetch prompts: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedPrompt(null);
    setFormData({
      name: '',
      description: '',
      prompt_type: 'system',
      content: '',
      json_schema: {},
      health_system_id: '',
      specialty: '',
      is_active: true,
      version: '1.0'
    });
    setIsEditing(true);
  };

  const handleCreateDefaultTemplate = () => {
    setSelectedPrompt(null);
    setFormData({
      name: 'default_system_message',
      description: 'Default system message template with comprehensive section library and JSON structure',
      prompt_type: 'system',
      content: DEFAULT_SYSTEM_MESSAGE_TEMPLATE,
      json_schema: {},
      health_system_id: '',
      specialty: '',
      is_active: true,
      version: '1.0'
    });
    setIsEditing(true);
  };

  const handleEdit = async (prompt) => {
    try {
      setLoading(true);
      setError(null);
      // Fetch full prompt details from server
      const response = await adminApi.get(`/admin/note/system/prompt/${prompt.id}/`);
      const fullPrompt = response.data;
      setSelectedPrompt(fullPrompt);
      setFormData({
        name: fullPrompt.name || '',
        description: fullPrompt.description || '',
        prompt_type: fullPrompt.prompt_type || 'system',
        content: fullPrompt.content || '',
        json_schema: fullPrompt.json_schema || {},
        health_system_id: fullPrompt.health_system_id || '',
        specialty: fullPrompt.specialty_id || '',
        is_active: fullPrompt.is_active !== undefined ? fullPrompt.is_active : true,
        version: fullPrompt.version || '1.0'
      });
      setIsEditing(true);
    } catch (err) {
      setError(`Failed to load prompt details: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      if (selectedPrompt) {
        // Update existing prompt
        await adminApi.put(`/admin/note/system/prompt/${selectedPrompt.id}/`, formData);
        setSuccessMessage(`Prompt "${formData.name}" updated successfully`);
      } else {
        // Create new prompt
        await adminApi.post('/admin/note/system/prompts/', formData);
        setSuccessMessage(`Prompt "${formData.name}" created successfully`);
      }
      
      await fetchPrompts();
      setIsEditing(false);
      setSelectedPrompt(null);
    } catch (err) {
      setError(`Failed to save prompt: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (prompt) => {
    setPromptToDelete(prompt);
  };
  const confirmDelete = async () => {
    if (!promptToDelete) return;
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      await adminApi.delete(`/admin/note/system/prompt/${promptToDelete.id}/`);
      await fetchPrompts();
      setSuccessMessage(`Prompt "${promptToDelete.name}" deleted successfully`);
      setPromptToDelete(null);
    } catch (err) {
      setError(`Failed to delete prompt: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };
  const cancelDelete = () => {
    setPromptToDelete(null);
  };

  const handleTest = async (prompt) => {
    try {
      setLoading(true);
      const testVariables = {};
      
      // Add default test variables based on prompt type
      if (prompt.prompt_type === 'system' || prompt.prompt_type === 'system_message') {
        testVariables.base_system_message = 'You are AnkrNoteLLM, a medical scribe assistant.';
      } else if (prompt.prompt_type === 'structured_json' || prompt.prompt_type === 'json_prompt') {
        testVariables.base_prompt = 'Generate a visit note from this kidney stone conversation: Patient reports severe left flank pain, nausea, and difficulty urinating. Physical exam shows CVA tenderness.';
      } else if (prompt.prompt_type === 'user_prompt') {
        testVariables.transcript = 'Sample medical conversation transcript for testing purposes.';
      } else if (prompt.prompt_type === 'fallback') {
        testVariables.base_prompt = 'Default fallback prompt for medical note generation.';
      }
      // Add common variables that might be used
      testVariables.patient_name = 'John Doe';
      testVariables.provider_name = 'Dr. Smith';
      testVariables.visit_date = new Date().toLocaleDateString();

      const response = await adminApi.post(`/admin/note/system/prompt/${prompt.id}/test/`, {
        variables: testVariables
      });
      
      setTestResult(response.data);
      setShowTestModal(true);
    } catch (err) {
      const errorDetail = err.response?.data?.detail || err.response?.data?.error || err.message;
      setError(`Failed to test prompt: ${errorDetail}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClearCache = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      await adminApi.post('/admin/system/prompts/cache/', {});
      setSuccessMessage('Prompt caches cleared successfully');
    } catch (err) {
      setError(`Failed to clear cache: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleAuditTrail = (promptId) => {
    setOpenAuditTrails(prev => {
      const newSet = new Set(prev);
      if (newSet.has(promptId)) {
        newSet.delete(promptId);
      } else {
        newSet.add(promptId);
      }
      return newSet;
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className='alert alert-warning d-flex justify-content-between align-items-center'>
        <div><i className='fas fa-warning me-1'></i>Make sure you know what you are doing. ALL ACTIONS ARE AUDIT-TRAILED.</div>
      </div>
      
      {/* Help Section */}
      {showHelp && (
        <div className="alert alert-info mb-4">
          <h6 className="alert-heading mb-3">
            <i className="fas fa-info-circle me-2"></i>Understanding LLM Prompt Templates
          </h6>
          <div className="row small">
            <div className="col-md-6">
              <strong>What you&apos;re creating:</strong>
              <ul className="mt-2 mb-3">
                <li><strong>System Messages:</strong> Base instructions that tell the AI how to behave (e.g., &ldquo;You are a medical scribe assistant&rdquo;)</li>
                <li><strong>Structured JSON:</strong> Templates that enforce specific output formats for consistent medical notes</li>
                <li><strong>User Prompts:</strong> Specific instructions for processing patient visit data</li>
                <li><strong>Fallback:</strong> Backup prompts when primary templates fail</li>
              </ul>
              <strong>How the pipeline works:</strong>
              <ol className="mt-2">
                <li>Patient visit occurs with audio transcription</li>
                <li>System selects appropriate prompt based on specialty/health system</li>
                <li>AI processes transcription using your template</li>
                <li>Generated note follows your structured format</li>
              </ol>
            </div>
            <div className="col-md-6">
              <strong>Template Variables:</strong>
              <p className="mt-2 mb-2">Use these placeholders in your prompts:</p>
              <ul className="mb-3">
                <li><code>{'{base_prompt}'}</code> - Base system instructions</li>
                <li><code>{'{transcription}'}</code> - Visit audio transcription</li>
                <li><code>{'{patient_info}'}</code> - Patient demographics</li>
                <li><code>{'{visit_type}'}</code> - Type of medical visit</li>
              </ul>
              <strong>Best Practices:</strong>
              <ul className="mb-0">
                <li>Use &ldquo;Load Default Template&rdquo; to start with comprehensive section library</li>
                <li>Test prompts before making them active</li>
                <li>Use specific health systems/specialties for targeted notes</li>
                <li>Keep prompts clear and medically appropriate</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {!isEditing && (
        <div className="d-flex justify-content-end align-items-center mb-4">
          <div>
            <button 
              className="btn btn-primary me-2" 
              onClick={handleCreateNew}
              disabled={loading}
            >
              <i className="fas fa-plus me-1"></i>New Prompt
            </button>
            <button 
              className="btn btn-outline-secondary" 
              onClick={handleClearCache}
              disabled={loading}
            >
              <i className="fas fa-trash me-1"></i>Clear Cache
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div>
          {error && (
            <div className="alert alert-danger alert-dismissible">
              {error}
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setError(null)}
              ></button>
            </div>
          )}

          {successMessage && (
            <div className="alert alert-success alert-dismissible">
              {successMessage}
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setSuccessMessage(null)}
              ></button>
            </div>
          )}

          {promptToDelete && (
            <div className="alert alert-warning">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <strong>Confirm Deletion</strong>
                  <p className="mb-2 mt-1">
                    Are you sure you want to delete the prompt &ldquo;<strong>{promptToDelete.name}</strong>&rdquo;? This action cannot be undone.
                  </p>
                  <button 
                    className="btn btn-danger btn-sm me-2"
                    onClick={confirmDelete}
                    disabled={loading}
                  >
                    {loading ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                    Delete
                  </button>
                  <button 
                    className="btn btn-outline-secondary btn-sm"
                    onClick={cancelDelete}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {showTestModal && testResult ? (
            /* Test Result View */
            <div className="card">
              <div className="card-header d-flex justify-content-between text-primary fs-5 align-items-center">
                <div className="text-primary fs-5">Prompt Test Result</div>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Prompt:</strong> {testResult.prompt_name || testResult.template_name}
                  </div>
                  <div className="col-md-6">
                    <strong>Type:</strong> {testResult.prompt_type || testResult.template_type}
                  </div>
                </div>
                
                <div className="mb-3">
                  <strong>Test Variables Used:</strong>
                  <pre className="bg-light p-3 mt-2 small border rounded">{JSON.stringify(testResult.test_variables, null, 2)}</pre>
                </div>
                
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Formatted Output:</strong>
                    <span className="badge bg-info">{testResult.content_length} characters</span>
                  </div>
                  <textarea 
                    className="form-control font-monospace" 
                    rows="15" 
                    value={testResult.formatted_content} 
                    readOnly 
                    style={{fontSize: '0.85rem'}}
                  />
                </div>

                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowTestModal(false)}
                >Close
                </button>
              </div>
            </div>
          ) : isEditing ? (
            /* Edit Form */
            <div className="card">
              <div className="card-header fs-5 text-primary  d-flex justify-content-between">
                  {selectedPrompt ? 'Edit Prompt' : 'Create New Prompt'}
              <button 
                className="btn btn-sm text-center" 
                onClick={() => setShowHelp(!showHelp)}
              >
                <i className={`fas fa-${showHelp ? 'chevron-up' : 'info-circle'} me-1`}></i>
                {showHelp ? 'Hide Help' : 'Learn More'}
              </button>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="e.g., default_system_message"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Prompt Type *
                        <i className="fas fa-lock ms-1 text-muted" title="Locked - all LLM prompts are system messages"></i>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value="System Message"
                        disabled
                        style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                      />
                      <div className="form-text text-muted small">
                        <i className="fas fa-info-circle me-1"></i>
                        All LLM prompt templates are system messages. The prompt_type field is locked to &lsquo;system&rsquo; because templates are retrieved by name, not type.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-8">
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        placeholder="Brief description of this prompt's purpose"
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label">Version</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.version}
                        onChange={(e) => setFormData({...formData, version: e.target.value})}
                        placeholder="e.g., 1.0, 2.1"
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Health System</label>
                      <select
                        className="form-control"
                        value={formData.health_system_id}
                        onChange={(e) => setFormData({...formData, health_system_id: e.target.value})}
                        disabled={loadingDropdowns}
                      >
                        <option value="">Global (All Health Systems)</option>
                        {healthSystems.map(hs => (
                          <option key={hs.id} value={hs.id}>{hs.name}</option>
                        ))}
                      </select>
                      {loadingDropdowns && (
                        <div className="form-text text-muted">
                          <span className="spinner-border spinner-border-sm me-1"></span>
                          Loading health systems...
                        </div>
                      )}
                      {!loadingDropdowns && (
                        <div className="form-text text-muted small">
                          Leave as &ldquo;Global&rdquo; to apply to all health systems, or select specific system for targeted prompts
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Specialty</label>
                      <select
                        className="form-control"
                        value={formData.specialty}
                        onChange={(e) => setFormData({...formData, specialty: e.target.value})}
                        disabled={loadingDropdowns}
                      >
                        <option value="">All Specialties</option>
                        {specialties.map(spec => (
                          <option key={spec.id} value={spec.id}>{spec.name}</option>
                        ))}
                      </select>
                      {loadingDropdowns && (
                        <div className="form-text text-muted">
                          <span className="spinner-border spinner-border-sm me-1"></span>
                          Loading specialties...
                        </div>
                      )}
                      {!loadingDropdowns && (
                        <div className="form-text text-muted small">
                          Leave as &ldquo;All Specialties&rdquo; for general use, or select specific specialty for specialized medical note formats
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                        />
                        <label className="form-check-label">
                          Active Prompt
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Prompt Content *</label>
                  <textarea
                    className="form-control font-monospace"
                    rows="15"
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    placeholder={formData.prompt_type === 'system' ?
                      DEFAULT_SYSTEM_MESSAGE_TEMPLATE :
                      '{base_prompt}\n\nPlease provide your response as a structured JSON object...'
                    }
                  />
                  <div className="form-text">
                    Use variables like {formData.prompt_type === 'system' ? '{base_system_message}' : '{base_prompt}'} that will be replaced at runtime
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <button 
                    className="btn btn-primary" 
                    onClick={handleSave}
                    disabled={loading || loadingDropdowns || !formData.name || !formData.content}
                  >
                    {loading ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                    {selectedPrompt ? 'Update' : 'Create'} Prompt
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    onClick={handleCreateDefaultTemplate}
                    disabled={loading}
                  >
                    <i className="fas fa-file-medical me-1"></i>Load Default Template
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => setIsEditing(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Prompts List */
            <>
              {loading && !prompts.length ? (
                <div className="text-center my-5">
                  <div className="spinner-border text-primary"></div>
                  <p className="mt-2">Loading prompts...</p>
                </div>
              ) : prompts.length === 0 ? (
                <div className="d-flex justify-content-center align-items-center" style={{minHeight:'400px'}}>
                  <div className="alert text-secondary text-center">
                    No system-level LLM prompts found. Admin needs to immediately create one or Sol&apos;s notetaking functionality may be severely impacted.
                  </div>
                </div>
              ) : (
                <div className="row">
                  {prompts.map((prompt) => (
                    <div key={prompt.id} className="col-12 mb-3">
                      <div className="card">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <h6 className="card-title d-flex align-items-center">
                                {prompt.name}
                                <span className={`badge ms-2 ${prompt.prompt_type === 'system' ? 'bg-primary' : 'bg-success'}`}>
                                  {prompt.prompt_type}
                                </span>
                                {!prompt.is_active && (
                                  <span className="badge bg-danger ms-1">Inactive</span>
                                )}
                              </h6>
                              
                              <p className="card-text small text-muted mb-2">
                                <strong>Preview:</strong> {prompt.content_preview}
                              </p>
                              
                              <p className="card-text small text-muted mb-2">
                                <strong>Description:</strong> {prompt.description || <em className="text-muted">None provided</em>}
                              </p>
                              
                              <div className="row small text-muted">
                                <div className="col-md-4">
                                  <strong>Health System:</strong> {prompt.health_system_name || 'Global'}
                                </div>
                                <div className="col-md-4">
                                  <strong>Specialty:</strong> {prompt.specialty_name || 'All'}
                                </div>
                                <div className="col-md-4">
                                  <strong>Version:</strong> {prompt.version || '1.0'}
                                </div>
                                <div className="col-md-6">
                                  <strong>Created:</strong> {formatDate(prompt.created_at)}
                                </div>
                                <div className="col-md-6">
                                  <strong> Updated:</strong> {formatDate(prompt.updated_at)}
                                </div>
                              </div>
                            </div>
                            
                            <div className="d-flex gap-1 flex-shrink-0">
                              <button
                                className="btn btn-sm btn-outline-success"
                                title="Test Prompt"
                                onClick={() => handleTest(prompt)}
                                disabled={loading}
                              >
                                <i className="fas fa-play"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                title="Edit Prompt"
                                onClick={() => handleEdit(prompt)}
                                disabled={loading}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                title="Delete Prompt"
                                onClick={() => handleDelete(prompt)}
                                disabled={loading}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                              <button
                                className={`btn btn-sm ${openAuditTrails.has(prompt.id) ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                title="Toggle Audit Trail"
                                onClick={() => toggleAuditTrail(prompt.id)}
                                disabled={loading}
                              >
                                <i className={`fas fa-${openAuditTrails.has(prompt.id) ? 'chevron-up' : 'history'}`}></i>
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Audit Trail Section */}
                        {openAuditTrails.has(prompt.id) && (
                          <div className="card-body mt-3 border-top pt-3">
                            <AuditLogs
                              resourceId={prompt.id}
                              resourceType="prompt"
                              title="Prompt Audit Trail"
                              showHeader={true}
                              maxHeight="400px"
                              pageSize={25}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

    </>
  );
};

export default SystemLLMPromptManagement;